<template>
  <transition name="modal-fade-scale">
    <div
      v-if="open && item"
      class="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm p-3"
      @click.self="emit('close')"
    >
      <div class="relative w-full max-w-6xl animate-modal-in">
        <div class="rounded-2xl overflow-hidden border border-white/10 bg-[#0c0c0e]/95 text-zinc-100 shadow-2xl ring-1 ring-sky-500/10">
          <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-4 py-3 bg-[#101013]/95 border-b border-white/5">
            <div class="min-w-0 flex items-center gap-3">
              <div class="hidden sm:grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-sky-500/10 ring-1 ring-sky-500/40 text-sky-400">
                <span class="material-symbols-outlined text-[18px]">image</span>
              </div>
              <div class="min-w-0">
                <p class="truncate font-medium leading-tight">{{ item.filename }}</p>
                <p class="text-xs text-zinc-400">{{ metaLabel }}</p>
              </div>
            </div>

            <div class="flex items-center gap-2 w-full sm:w-auto flex-wrap">
              <button class="btn-subtle" @click="copyLink" :disabled="copying || !fullUrl">
                <span class="material-symbols-outlined text-[18px] -ml-0.5">link</span>
                <span class="hidden sm:inline">{{ copying ? 'Copied' : 'Copy link' }}</span>
              </button>

              <a v-if="fullUrl" :href="fullUrl" download class="btn-subtle">
                <span class="material-symbols-outlined text-[18px] -ml-0.5">download</span>
                <span class="hidden sm:inline">Download</span>
              </a>

              <button class="btn-ghost ml-auto" @click="emit('close')">
                <span class="material-symbols-outlined text-[18px]">close</span>
              </button>
            </div>
          </div>

          <div class="grid gap-0 md:grid-cols-[minmax(0,1.8fr)_minmax(0,1.1fr)] bg-[#09090b]">
            <div class="relative bg-[#0b0b0e]">
              <button class="nav-btn left-3" @click.stop="emit('prev')" aria-label="Previous">
                <span class="material-symbols-outlined">chevron_left</span>
              </button>

              <button class="nav-btn right-3" @click.stop="emit('next')" aria-label="Next">
                <span class="material-symbols-outlined">chevron_right</span>
              </button>

              <div class="grid place-items-center max-h-[78vh] overflow-hidden">
                <div v-if="isImage" class="relative">
                  <img
                    :src="fullUrl"
                    :alt="item.filename"
                    class="select-none will-change-transform max-h-[78vh]"
                    :style="imgStyle"
                    @error="onErr"
                    @wheel.passive="onWheel"
                    @mousedown="onDown"
                    @dblclick.prevent="resetView"
                    draggable="false"
                    ref="imgEl"
                  />
                </div>

                <div
                  v-else
                  class="h-[320px] w-full flex flex-col items-center justify-center text-sm text-zinc-400 gap-2"
                >
                  <div class="h-12 w-12 rounded-2xl bg-sky-500/10 flex items-center justify-center mb-1 ring-1 ring-sky-500/30 text-sky-300">
                    <span class="material-symbols-outlined text-[26px]">insert_drive_file</span>
                  </div>
                  <p>Preview not available for this file type.</p>
                  <p class="text-xs opacity-70">Use download to open the file.</p>
                </div>
              </div>

              <div v-if="isImage" class="pointer-events-none absolute inset-x-0 bottom-0 flex justify-center">
                <div class="pointer-events-auto mb-4 flex items-center gap-2 rounded-full bg-[#07070a]/90 px-2 py-1.5 ring-1 ring-white/10 shadow-lg">
                  <button class="ctl" @click.stop="zoomOut">−</button>
                  <div class="text-xs tabular-nums w-16 text-center">{{ Math.round(scale * 100) }}%</div>
                  <button class="ctl" @click.stop="zoomIn">+</button>
                  <div class="h-4 w-px bg-white/10 mx-1"></div>
                  <button class="btn-chip" @click.stop="resetView">Reset</button>
                </div>
              </div>
            </div>

            <div class="flex flex-col border-l border-white/5 bg-[#111113] max-h-[78vh]">
              <div class="px-4 pt-4 pb-3 border-b border-white/5">
                <p class="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1">File details</p>
                <div class="grid grid-cols-2 gap-3 text-xs text-zinc-300">
                  <div>
                    <p class="text-zinc-500 text-[11px] uppercase tracking-[0.16em] mb-0.5">Name</p>
                    <p class="truncate">{{ item.filename }}</p>
                  </div>

                  <div>
                    <p class="text-zinc-500 text-[11px] uppercase tracking-[0.16em] mb-0.5">Size</p>
                    <p>{{ sizeLabel }}</p>
                  </div>

                  <div>
                    <p class="text-zinc-500 text-[11px] uppercase tracking-[0.16em] mb-0.5">Created</p>
                    <p>{{ createdLabel }}</p>
                  </div>

                  <div v-if="dimensions">
                    <p class="text-zinc-500 text-[11px] uppercase tracking-[0.16em] mb-0.5">Dimensions</p>
                    <p>{{ dimensions }}</p>
                  </div>
                </div>
              </div>

              <div class="flex-1 overflow-y-auto px-4 py-4 custom-scroll">
                <div class="flex items-center justify-between gap-2 mb-3">
                  <div>
                    <p class="text-[11px] uppercase tracking-[0.18em] text-zinc-500">Palette HEX</p>
                    <p class="text-xs text-zinc-400 mt-0.5">HEX codes linked to this file.</p>
                  </div>

                  <button v-if="hexList.length" class="btn-chip text-[11px]" @click="copyHexList">
                    Copy HEX
                  </button>
                </div>

                <div v-if="!hexList.length" class="text-xs text-zinc-500 mt-2">
                  No HEX codes saved for this file.
                </div>

                <div v-else class="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-1">
                  <div
                    v-for="hex in hexList"
                    :key="hex"
                    class="rounded-xl border border-white/10 bg-[#0d0d10] p-2.5 flex items-center gap-2"
                  >
                    <div
                      class="h-7 w-7 rounded-lg border border-white/10 shrink-0"
                      :style="{ backgroundColor: hex }"
                    ></div>
                    <div class="min-w-0">
                      <p class="text-xs font-mono">{{ hex }}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div class="hidden sm:flex items-center justify-between px-4 py-2.5 bg-[#101013] text-xs text-zinc-400 border-t border-white/5">
                <p v-if="isImage">Drag to move • scroll to zoom • double click to reset • Esc to close</p>
                <p v-else>Use download to open the original file • Esc to close</p>
                <p v-if="isImage && dimensions">{{ dimensions }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import axios from 'axios'

const props = defineProps({
  open: { type: Boolean, default: false },
  item: { type: Object, default: null }
})
const emit = defineEmits(['close', 'next', 'prev'])

const imgEl = ref(null)
const copying = ref(false)

function full(u) {
  if (!u) return ''
  if (props.item?.absoluteUrl) return props.item.absoluteUrl
  if (/^https?:\/\//i.test(u)) return u
  const base = axios.defaults.baseURL || ''
  return `${base}${u.startsWith('/') ? u : `/${u}`}`
}

const fullUrl = computed(() => full(props.item?.url || ''))

const sizeLabel = computed(() => {
  const s = Number(props.item?.size || 0)
  if (s >= 1 << 20) return (s / (1 << 20)).toFixed(1) + ' MB'
  if (s >= 1 << 10) return (s / (1 << 10)).toFixed(1) + ' KB'
  return s + ' B'
})

const createdLabel = computed(() => {
  const d = props.item?.createdAt ? new Date(props.item.createdAt) : null
  if (!d) return ''
  return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
})

const metaLabel = computed(() => {
  return [createdLabel.value, sizeLabel.value].filter(Boolean).join(' · ')
})

const isImage = computed(() =>
  /\.(png|jpe?g|gif|webp|bmp|svg|avif)$/i.test(props.item?.filename || '')
)

const hexList = computed(() => {
  const it = props.item || {}
  const raw =
    it.hexes ??
    it.hexCodes ??
    it.colors ??
    it.hex ??
    null

  if (!raw) return []

  if (Array.isArray(raw)) {
    return raw
      .map(String)
      .map(h => h.trim())
      .filter(Boolean)
  }

  if (typeof raw === 'string') {
    return raw
      .split(/[,\s]+/)
      .map(h => h.trim())
      .filter(Boolean)
  }

  return []
})

function copyHexList() {
  if (!hexList.value.length) return
  const text = hexList.value.join(', ')
  navigator.clipboard?.writeText(text).then(() => {
    copying.value = true
    setTimeout(() => {
      copying.value = false
    }, 900)
  })
}

function onErr(e) {
  e.target.src = ''
}

function copyLink() {
  if (!fullUrl.value) return
  navigator.clipboard?.writeText(fullUrl.value).then(() => {
    copying.value = true
    setTimeout(() => (copying.value = false), 900)
  })
}

const scale = ref(1)
const tx = ref(0)
const ty = ref(0)
const dragging = ref(false)
let startX = 0
let startY = 0
let baseX = 0
let baseY = 0

const imgStyle = computed(() => ({
  transform: `translate(${tx.value}px, ${ty.value}px) scale(${scale.value})`,
  transition: dragging.value ? 'none' : 'transform .15s ease',
  maxHeight: '78vh',
  width: 'auto'
}))

const dimensions = computed(() => {
  const el = imgEl.value
  if (!el || !isImage.value) return ''
  return `${el.naturalWidth || ''}×${el.naturalHeight || ''} px`
})

function resetView() {
  scale.value = 1
  tx.value = 0
  ty.value = 0
}

function zoomIn() {
  scale.value = Math.min(8, +(scale.value * 1.2).toFixed(3))
}

function zoomOut() {
  scale.value = Math.max(0.2, +(scale.value / 1.2).toFixed(3))
}

function onWheel(e) {
  if (!isImage.value) return
  e.preventDefault()
  e.deltaY < 0 ? zoomIn() : zoomOut()
}

function onDown(e) {
  if (!isImage.value || scale.value <= 1) return
  dragging.value = true
  startX = e.clientX
  startY = e.clientY
  baseX = tx.value
  baseY = ty.value

  const move = ev => {
    tx.value = baseX + (ev.clientX - startX)
    ty.value = baseY + (ev.clientY - startY)
  }
  const up = () => {
    dragging.value = false
    window.removeEventListener('mousemove', move)
    window.removeEventListener('mouseup', up)
  }
  window.addEventListener('mousemove', move)
  window.addEventListener('mouseup', up)
}

function onKey(e) {
  if (!props.open) return
  if (e.key === 'Escape') emit('close')
  if (e.key === 'ArrowRight') emit('next')
  if (e.key === 'ArrowLeft') emit('prev')
}

watch(
  () => props.open,
  v => {
    if (v) {
      resetView()
    }
  }
)

watch(
  () => props.item?.url,
  () => {
    resetView()
  }
)

onMounted(() => {
  window.addEventListener('keydown', onKey)
})

onUnmounted(() => {
  window.removeEventListener('keydown', onKey)
})
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity .18s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.btn-subtle {
  @apply inline-flex items-center gap-1.5 h-8 px-2 rounded-md border border-white/10 bg-[#171719]/50 hover:bg-[#2b2b2d]/70 text-xs transition;
}

.btn-ghost {
  @apply inline-flex items-center h-8 w-8 justify-center rounded-md hover:bg-white/10 transition;
}

.btn-chip {
  @apply inline-flex items-center h-7 px-3 rounded-full bg-white/10 hover:bg-white/15 text-xs;
}

.ctl {
  @apply h-7 w-7 grid place-items-center rounded-md bg-white/10 hover:bg-white/15;
}

.nav-btn {
  @apply hidden sm:grid absolute top-1/2 -translate-y-1/2 h-10 w-10 place-items-center rounded-full bg-white/80 text-zinc-900 hover:bg-white shadow ring-1 ring-zinc-300;
}

.custom-scroll::-webkit-scrollbar {
  width: 6px;
}
.custom-scroll::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scroll::-webkit-scrollbar-thumb {
  background: rgba(148, 163, 184, 0.9);
  border-radius: 999px;
}
</style>
